import { type ChangeEvent, type FormEvent, useEffect, useState } from "react"
import { router, useForm } from "@inertiajs/react"
import {
  DndContext,
  type DragEndEvent,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core"
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { GripVertical, ImageIcon, Pencil, Plus, Trash2 } from "lucide-react"

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardAction, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import {
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  ResponsiveDrawer,
} from "@/components/ui/drawer"
import { InlineInput } from "@/components/ui/inline-input"
import { InlineSelect } from "@/components/ui/inline-select"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { t } from "@/lib/i18n"
import { patchInline, toastInlineUpdateError } from "@/lib/inline-update"
import { cn } from "@/lib/utils"

// Group rule helpers live in a domain-neutral module so the diner dish detail
// shares the exact same wording/logic. Imported for use in the card below and
// re-exported for the staff surfaces that already import them from this file.
import { groupRuleSummary, groupSelectionValid, type ExtraGroupRules } from "@/features/extras/group-rules"
export { groupRuleSummary, groupSelectionValid, type ExtraGroupRules }

export type Extra = {
  id: number
  name: string
  price: string
  status: string
  imageUrl: string | null
}

export type ExtraGroup = {
  id: number
  name: string
  minSelect: number
  maxSelect: number | null
  status: string
  itemIds: string[]
  extras: Extra[]
}

type Option = { value: string; label: string }

export type ExtrasWorkspaceProps = {
  groups: ExtraGroup[]
  extraStatuses: Option[]
}

type GroupDrawerState = { mode: "create" } | { mode: "edit"; group: ExtraGroup } | null

export function ExtrasWorkspace({ groups, extraStatuses }: ExtrasWorkspaceProps) {
  const [drawer, setDrawer] = useState<GroupDrawerState>(null)

  return (
    <Card density="compact" data-testid="extras-workspace">
      <CardHeader className="border-b">
        <CardTitle className="text-base">
          {t("ui.react.pages.staff.location.manage.menu.index.extras_title")}
        </CardTitle>
        <CardDescription>
          {t("ui.react.pages.staff.location.manage.menu.index.extras_description")}
        </CardDescription>
        <CardAction>
          <Button
            type="button"
            size="sm"
            onClick={() => setDrawer({ mode: "create" })}
            data-testid="add-extra-group-button"
          >
            <Plus />
            {t("ui.react.pages.staff.location.manage.menu.index.add_extra_group")}
          </Button>
        </CardAction>
      </CardHeader>

      <CardContent className="space-y-4">
        {groups.length === 0 ? (
          <p className="px-1 py-6 text-center text-sm text-muted-foreground">
            {t("ui.react.pages.staff.location.manage.menu.index.no_extra_groups_yet")}
          </p>
        ) : (
          groups.map((group) => (
            <ExtraGroupCard
              key={group.id}
              group={group}
              extraStatuses={extraStatuses}
              onEdit={() => setDrawer({ mode: "edit", group })}
            />
          ))
        )}
      </CardContent>

      {drawer && (
        <ExtraGroupFormDrawer
          key={drawer.mode === "edit" ? `edit-${drawer.group.id}` : "create"}
          state={drawer}
          onClose={() => setDrawer(null)}
        />
      )}
    </Card>
  )
}

function ExtraGroupCard({
  group,
  extraStatuses,
  onEdit,
}: {
  group: ExtraGroup
  extraStatuses: Option[]
  onEdit: () => void
}) {
  const [deleteOpen, setDeleteOpen] = useState(false)

  return (
    <div className="space-y-3 rounded-lg border p-3" data-testid={`extra-group-${group.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <p className="truncate font-medium" data-testid={`extra-group-name-${group.id}`}>
              {group.name}
            </p>
            {group.status === "inactive" && (
              <Badge variant="outline">
                {t("ui.react.pages.staff.location.manage.menu.index.extra_group_status_inactive")}
              </Badge>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            {groupRuleSummary(group)} · {group.extras.length}
          </p>
        </div>

        <div className="flex shrink-0 items-center gap-1">
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            onClick={onEdit}
            data-testid={`edit-extra-group-${group.id}`}
            aria-label={t("ui.react.pages.staff.location.manage.menu.index.edit_extra_group_title")}
          >
            <Pencil />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            onClick={() => setDeleteOpen(true)}
            data-testid={`delete-extra-group-${group.id}`}
            aria-label={t("ui.react.pages.staff.location.manage.menu.index.delete_extra_group")}
          >
            <Trash2 className="text-destructive" />
          </Button>
        </div>
      </div>

      <ExtrasList groupId={group.id} extras={group.extras} extraStatuses={extraStatuses} />
      <AddExtraForm groupId={group.id} />

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent data-testid={`delete-extra-group-dialog-${group.id}`}>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("ui.react.pages.staff.location.manage.menu.index.delete_extra_group")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("ui.react.pages.staff.location.manage.menu.index.delete_extra_group_confirm")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`delete-extra-group-dialog-cancel-${group.id}`}>
              {t("ui.common.actions.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() =>
                router.delete(`/manage/extra_groups/${group.id}`, {
                  preserveScroll: true,
                  preserveState: true,
                })
              }
              data-testid={`delete-extra-group-dialog-confirm-${group.id}`}
            >
              {t("ui.react.pages.staff.location.manage.menu.index.delete_extra_group")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

function ExtrasList({
  groupId,
  extras,
  extraStatuses,
}: {
  groupId: number
  extras: Extra[]
  extraStatuses: Option[]
}) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }))
  const [order, setOrder] = useState<number[]>(extras.map((extra) => extra.id))
  const [reordering, setReordering] = useState(false)

  useEffect(() => {
    const incoming = extras.map((extra) => extra.id)
    // Re-sync only when extras are added or removed. A reorder response echoes
    // back the order we just applied optimistically, so re-syncing on it would
    // only make the list flicker; a rejected reorder is rolled back explicitly
    // by handleDragEnd instead.
    setOrder((current) => {
      const sameSet =
        current.length === incoming.length && incoming.every((id) => current.includes(id))
      return sameSet ? current : incoming
    })
  }, [extras])

  if (extras.length === 0) {
    return (
      <p className="px-1 text-sm text-muted-foreground">
        {t("ui.react.pages.staff.location.manage.menu.index.no_extras_yet")}
      </p>
    )
  }

  const orderedExtras = order
    .map((id) => extras.find((extra) => extra.id === id))
    .filter((extra): extra is Extra => extra != null)

  const handleDragEnd = async (event: DragEndEvent) => {
    // A drop while the previous reorder is still in flight would compute its
    // move against a stale `order` and race the response back to the server.
    if (reordering) return

    const { active, over } = event
    if (!over || active.id === over.id) return

    const oldIndex = order.indexOf(Number(active.id))
    const newIndex = order.indexOf(Number(over.id))
    if (oldIndex === -1 || newIndex === -1) return

    const previous = order
    const next = arrayMove(order, oldIndex, newIndex)
    setOrder(next)
    setReordering(true)

    // One request for the whole list: the server renumbers the group inside a
    // single transaction, so the list either lands in the new order or stays
    // exactly as it was. On failure the optimistic order is rolled back, so
    // what staff sees never drifts from what is stored.
    try {
      await patchInline(
        `/manage/extra_groups/${groupId}/extras/reorder`,
        { extra_ids: next },
        { preserveState: true }
      )
    } catch (error: unknown) {
      setOrder(previous)
      toastInlineUpdateError(error)
    } finally {
      setReordering(false)
    }
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={order} strategy={verticalListSortingStrategy}>
        <ul className="divide-y rounded-md border" data-testid={`extras-list-${groupId}`}>
          {orderedExtras.map((extra) => (
            <SortableExtraRow key={extra.id} groupId={groupId} extra={extra} statuses={extraStatuses} />
          ))}
        </ul>
      </SortableContext>
    </DndContext>
  )
}

function SortableExtraRow({
  groupId,
  extra,
  statuses,
}: {
  groupId: number
  extra: Extra
  statuses: Option[]
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: extra.id,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={cn("flex items-center gap-2 px-2 py-2", isDragging && "opacity-60")}
      data-testid={`extra-row-${extra.id}`}
    >
      <button
        type="button"
        className="shrink-0 cursor-grab touch-none text-muted-foreground active:cursor-grabbing"
        aria-label="Drag to reorder"
        data-testid={`extra-drag-handle-${extra.id}`}
        {...attributes}
        {...listeners}
      >
        <GripVertical className="size-4" />
      </button>

      {extra.imageUrl ? (
        <img src={extra.imageUrl} alt={extra.name} className="size-8 shrink-0 rounded object-cover" />
      ) : (
        <div className="flex size-8 shrink-0 items-center justify-center rounded border border-dashed text-muted-foreground">
          <ImageIcon className="size-3.5" />
        </div>
      )}

      <InlineInput
        value={extra.name}
        onSubmit={(name) =>
          patchInline(`/manage/extra_groups/${groupId}/extras/${extra.id}`, { name }, { preserveState: true })
        }
        className="h-8 min-w-0 flex-1"
        data-testid={`extra-name-${extra.id}`}
        aria-label={t("ui.react.pages.staff.location.manage.menu.index.extra_name_placeholder")}
      />

      <InlineInput
        type="number"
        min="0"
        step="0.01"
        inputMode="decimal"
        value={extra.price}
        onSubmit={(price) =>
          patchInline(`/manage/extra_groups/${groupId}/extras/${extra.id}`, { price }, { preserveState: true })
        }
        className="h-8 w-20 shrink-0 tabular-nums"
        data-testid={`extra-price-${extra.id}`}
        aria-label={t("ui.react.pages.staff.location.manage.menu.index.extra_price")}
      />

      <InlineSelect
        value={extra.status}
        options={statuses.map((status) => ({
          value: status.value,
          label: status.label,
          testId: `extra-status-${extra.id}-option-${status.value}`,
        }))}
        onSubmit={(status) =>
          patchInline(`/manage/extra_groups/${groupId}/extras/${extra.id}`, { status }, { preserveState: true })
        }
        size="sm"
        className="w-28 shrink-0"
        data-testid={`extra-status-${extra.id}`}
      />

      <DeleteExtraButton groupId={groupId} extraId={extra.id} />
    </li>
  )
}

function DeleteExtraButton({ groupId, extraId }: { groupId: number; extraId: number }) {
  const [open, setOpen] = useState(false)

  return (
    <>
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        onClick={() => setOpen(true)}
        data-testid={`delete-extra-${extraId}`}
        aria-label={t("ui.react.pages.staff.location.manage.menu.index.delete_extra")}
      >
        <Trash2 className="text-destructive" />
      </Button>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent data-testid={`delete-extra-dialog-${extraId}`}>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("ui.react.pages.staff.location.manage.menu.index.delete_extra")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("ui.react.pages.staff.location.manage.menu.index.delete_extra_confirm")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`delete-extra-dialog-cancel-${extraId}`}>
              {t("ui.common.actions.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() =>
                router.delete(`/manage/extra_groups/${groupId}/extras/${extraId}`, {
                  preserveScroll: true,
                  preserveState: true,
                })
              }
              data-testid={`delete-extra-dialog-confirm-${extraId}`}
            >
              {t("ui.react.pages.staff.location.manage.menu.index.delete_extra")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

function AddExtraForm({ groupId }: { groupId: number }) {
  const { data, setData, post, processing, errors, reset } = useForm<{
    name: string
    price: string
    image: File | null
  }>({ name: "", price: "", image: null })

  const submit = (event: FormEvent) => {
    event.preventDefault()
    post(`/manage/extra_groups/${groupId}/extras`, {
      forceFormData: true,
      preserveScroll: true,
      preserveState: true,
      onSuccess: () => reset("name", "price", "image"),
    })
  }

  const handleImageChange = (event: ChangeEvent<HTMLInputElement>) => {
    setData("image", event.target.files?.[0] ?? null)
  }

  return (
    <form onSubmit={submit} className="flex flex-wrap items-center gap-1.5 pt-1" data-testid={`add-extra-form-${groupId}`}>
      <Input
        value={data.name}
        onChange={(event) => setData("name", event.target.value)}
        placeholder={t("ui.react.pages.staff.location.manage.menu.index.extra_name_placeholder")}
        className="h-8 min-w-0 flex-1"
        data-testid={`add-extra-name-${groupId}`}
      />
      <Input
        type="number"
        min="0"
        step="0.01"
        inputMode="decimal"
        value={data.price}
        onChange={(event) => setData("price", event.target.value)}
        placeholder="0.00"
        className="h-8 w-20 shrink-0"
        data-testid={`add-extra-price-${groupId}`}
      />
      <Input
        type="file"
        accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
        onChange={handleImageChange}
        className="h-8 w-40 shrink-0 text-xs"
        data-testid={`add-extra-image-${groupId}`}
      />
      <Button
        type="submit"
        size="icon-sm"
        variant="outline"
        disabled={processing || data.name.trim() === "" || data.price.trim() === ""}
        data-testid={`add-extra-button-${groupId}`}
        aria-label={t("ui.react.pages.staff.location.manage.menu.index.add_extra")}
      >
        <Plus />
      </Button>
      {(errors.name || errors.price) && (
        <p className="w-full text-xs font-medium text-destructive">{errors.name || errors.price}</p>
      )}
    </form>
  )
}

function ExtraGroupFormDrawer({
  state,
  onClose,
}: {
  state: NonNullable<GroupDrawerState>
  onClose: () => void
}) {
  const editing = state.mode === "edit"
  const group = editing ? state.group : null

  const { data, setData, post, patch, processing, errors } = useForm<{
    name: string
    min_select: string
    max_select: string
    status: string
  }>({
    name: group?.name ?? "",
    min_select: group ? String(group.minSelect) : "0",
    max_select: group?.maxSelect != null ? String(group.maxSelect) : "",
    status: group?.status ?? "active",
  })

  // A required minimum that outnumbers the group's active extras can never be
  // satisfied: diners see the item as unavailable and staff hit a builder
  // rejection. Warn rather than block — the group is created before its extras,
  // so this is a legitimate intermediate state while they're being added.
  const activeExtraCount = group?.extras.filter((extra) => extra.status === "active").length ?? 0
  const minSelect = Number(data.min_select)
  const minSelectUnsatisfiable = Number.isFinite(minSelect) && minSelect > activeExtraCount

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()

    const options = {
      preserveScroll: true,
      preserveState: true,
      onSuccess: () => onClose(),
    }

    // This form sends a JSON body (no forceFormData), so `_method` spoofing
    // wouldn't reach Rack::MethodOverride — issue a real PATCH on edit.
    if (editing) {
      patch(`/manage/extra_groups/${group!.id}`, options)
    } else {
      post("/manage/extra_groups", options)
    }
  }

  return (
    <ResponsiveDrawer open onOpenChange={(next) => !next && onClose()}>
      <DrawerContent className="data-[vaul-drawer-direction=right]:sm:max-w-md">
        <DrawerHeader>
          <DrawerTitle>
            {editing
              ? t("ui.react.pages.staff.location.manage.menu.index.edit_extra_group_title")
              : t("ui.react.pages.staff.location.manage.menu.index.add_extra_group")}
          </DrawerTitle>
          <DrawerDescription>
            {t("ui.react.pages.staff.location.manage.menu.index.extras_description")}
          </DrawerDescription>
        </DrawerHeader>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col" data-testid="extra-group-form">
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4">
            <div className="space-y-2">
              <Label htmlFor="extra-group-name">
                {t("ui.react.pages.staff.location.manage.menu.index.extra_group_name")}
              </Label>
              <Input
                id="extra-group-name"
                value={data.name}
                onChange={(event) => setData("name", event.target.value)}
                data-testid="extra-group-name-input"
                required
              />
              {errors.name && <p className="text-sm font-medium text-destructive">{errors.name}</p>}
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="extra-group-min">
                  {t("ui.react.pages.staff.location.manage.menu.index.extra_group_min_select")}
                </Label>
                <Input
                  id="extra-group-min"
                  type="number"
                  min="0"
                  step="1"
                  value={data.min_select}
                  onChange={(event) => setData("min_select", event.target.value)}
                  data-testid="extra-group-min-input"
                />
                {errors.min_select && (
                  <p className="text-sm font-medium text-destructive">{errors.min_select}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="extra-group-max">
                  {t("ui.react.pages.staff.location.manage.menu.index.extra_group_max_select")}
                </Label>
                <Input
                  id="extra-group-max"
                  type="number"
                  min="1"
                  step="1"
                  value={data.max_select}
                  onChange={(event) => setData("max_select", event.target.value)}
                  data-testid="extra-group-max-input"
                />
                {errors.max_select && (
                  <p className="text-sm font-medium text-destructive">{errors.max_select}</p>
                )}
              </div>
            </div>

            {minSelectUnsatisfiable && (
              <p
                className="rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-sm text-warning"
                data-testid="extra-group-min-select-warning"
              >
                {t("ui.react.pages.staff.location.manage.menu.index.extra_group_min_select_warning", {
                  min: minSelect,
                  count: activeExtraCount,
                })}
              </p>
            )}

            <div className="space-y-2">
              <Label htmlFor="extra-group-status">
                {t("ui.react.pages.staff.location.manage.menu.index.extra_group_status")}
              </Label>
              <Select value={data.status} onValueChange={(value) => setData("status", value)}>
                <SelectTrigger id="extra-group-status" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">
                    {t("ui.react.pages.staff.location.manage.menu.index.extra_group_status_active")}
                  </SelectItem>
                  <SelectItem value="inactive">
                    {t("ui.react.pages.staff.location.manage.menu.index.extra_group_status_inactive")}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DrawerFooter className="flex-row justify-end gap-2 border-t">
            <DrawerClose asChild>
              <Button type="button" variant="outline">
                {t("ui.react.pages.staff.location.manage.items.new.cancel")}
              </Button>
            </DrawerClose>
            <Button type="submit" disabled={processing} data-testid="save-extra-group-button">
              {editing
                ? t("ui.react.components.ui.inline_update.save")
                : t("ui.react.pages.staff.location.manage.menu.index.create_extra_group")}
            </Button>
          </DrawerFooter>
        </form>
      </DrawerContent>
    </ResponsiveDrawer>
  )
}
